/**
 * @typedef {Object} ISettingsFiltersState
 *
 * @property {Object} fuelsRequired
 * @property {boolean} fuelsRequired.electricity
 * @property {boolean} fuelsRequired.gas
 * @property {boolean} hasSolarPanels
 */

import * as React from 'react';
import debounce from 'lodash/debounce';
import { interaction, navigation } from '@origin-digital/reporting-client';
import './settings-filters.scss';
import { getValidationError, POSTCODE_VALIDATION_RULES } from '../../services/validation';
import { isFuelEligibleForPurchase, mapPropsToState } from './utils';
import * as reducers from './reducers';
import ACTIONS from '../../constants/actions';
import { FUEL_MAPPING } from '../../constants/energy-usage';
import { IComparisonPlan } from '@origin-digital/plans';

import { Filters } from './components/filters';
import { ConnectionAddressObject } from '@origin-digital/address-lookup';

export function getPostCodeValidationError(event: React.ChangeEvent<HTMLInputElement>): string {
  const { value } = event.target;
  return getValidationError(value, POSTCODE_VALIDATION_RULES);
}

export interface IFilterChangeEvent {
  requiresElectricity: boolean;
  requiresGas: boolean;
  hasSolarPanels: boolean;
  renewableEnergy: {
    gas: number;
    electricity: number;
  };
  servicesRequired: {
    electricity: boolean;
    gas: boolean;
  };
  isCustomerMoving: boolean;
  searchByPostcode: boolean;
  postcode: string;
  connectionScenario: string;
}

interface ISettingsFilters extends IFilterChangeEvent {
  isBusiness: boolean;
  isCustomerMoving: boolean;
  onChange: (a: IFilterChangeEvent, b: string) => void;
  lpgCrossSellLink: string;
  fuelEligibilityStatus: {
    electricity: string;
    gas: string;
    postcode?: number;
  };
  userRegion?: string;
  plans?: IComparisonPlan[];
  addressApiProps: {
    url: string;
    apiKey: string;
    qasAuthToken: string;
  };
  updatePlans: (address?: ConnectionAddressObject, id?: string) => void;
  address?: ConnectionAddressObject;
}

interface ISettingsFiltersState extends ISettingsFilters {
  isBusiness: boolean;
  hasSolarPanels: boolean;
  isCustomerMoving: boolean;
  fuelsRequired: {
    electricity: boolean;
    gas: boolean;
  };
}

export default class SettingsFilters extends React.Component<
  ISettingsFilters,
  ISettingsFiltersState
> {
  constructor(props) {
    super(props);
    /**
     * @type {SettingsFiltersState}
     */
    this.state = {
      ...mapPropsToState({ ...props }),
    };

    /**
     * We don't need to do this every time there's an interaction, let's
     * keep the invocation quantity to a reasonable number per second.
     */
    this.notifyParentOfFilterChanges = debounce(this.notifyParentOfFilterChanges, 333);
  }

  componentDidMount() {
    navigation('screen', {
      currentUri: window.location.href,
      friendlyUri: this.state.isBusiness
        ? 'originenergy:business:electricity-and-gas:plans:energy-plans'
        : 'originenergy:electricity-gas:plans',
    });
  }

  /**
   * Here we sync our internal state when a parent gives us new data.
   * @param {SettingsFilters.propTypes} newProps
   */
  componentWillReceiveProps(newProps: ISettingsFilters) {
    this.setState(() => mapPropsToState(newProps));
  }

  onClickRenewableEnergyToolTip = () => {
    interaction('click', {
      componentType: 'button',
      id: 'green-options',
      parentContainer: 'green-options',
      appName: 'compare-plans',
      label: 'Want to go green?',
    });
  };

  /**
   * Here we sync our internal state when a parent gives us new data.
   * @param {String} changeDescriptor
   */
  notifyParentOfFilterChanges = (filterChangeAction = ACTIONS.UPDATE_PLAN) => {
    this.validateFuelsRequired(); // at least one fuel must be selected

    this.props.onChange(
      {
        requiresElectricity: this.state.fuelsRequired.electricity,
        requiresGas: this.state.fuelsRequired.gas,
        hasSolarPanels: this.state.hasSolarPanels,
        renewableEnergy: this.state.renewableEnergy,
        isCustomerMoving: this.state.isCustomerMoving,
        postcode: this.state.postcode,
      },
      filterChangeAction,
    );
  };

  validateFuelsRequired = () => {
    const { fuelsRequired } = this.state;
    if (!fuelsRequired.electricity && !fuelsRequired.gas) {
      this.setState((state) => reducers.fuelRequirementChanged('electricity', true, state));
      this.setState((state) => reducers.fuelRequirementChanged('gas', true, state));
    }
  };

  handleFuelRequirementChange = (whichFuel, newValue) => {
    interaction('click', {
      componentType: 'checkbox',
      id: 'productType',
      parentContainer: 'productType',
      appName: 'compare-plans',
      label: FUEL_MAPPING[whichFuel],
      state: newValue ? 'selected' : 'unselected',
    });

    this.setState(
      (state) => reducers.fuelRequirementChanged(whichFuel, newValue, state),
      this.notifyParentOfFilterChanges,
    );
  };

  handleRenewableEnergyChange = (whichFuel, newValue) => {
    interaction('click', {
      componentType: 'selectlist',
      id: 'green-options',
      parentContainer: 'green-options',
      appName: 'compare-plans',
      label: FUEL_MAPPING[whichFuel],
      state: `${newValue}%`,
    });

    this.setState(
      (state) => reducers.renewableEnergyChanged(whichFuel, newValue, state),
      this.notifyParentOfFilterChanges,
    );
  };

  handleGasBottleLinkClick = () => {
    interaction('click', {
      componentType: 'link',
      id: 'productType',
      parentContainer: 'productType',
      appName: 'compare-plans',
      label: 'Looking for gas bottles?',
    });
  };

  handleHasSolarPanelsChange = (event) => {
    const hasSolarPanels = event.target.checked;

    interaction('click', {
      componentType: 'checkbox',
      id: 'productType',
      parentContainer: 'productType',
      appName: 'compare-plans',
      label: 'You have solar panels',
      state: hasSolarPanels ? 'selected' : 'unselected',
    });

    this.setState(
      (state) => reducers.hasSolarPanelsChanged(hasSolarPanels, state),
      this.notifyParentOfFilterChanges,
    );
  };

  handlePostcodeChange = (event, isValid) => {
    const newValue = event.target.value;

    if (isValid) {
      interaction('click', {
        componentType: 'selectlist',
        id: 'postcode-search',
        parentContainer: 'postcode-search',
        appName: 'compare-plans',
        label: 'Your postcode',
        state: newValue,
      });
    }

    this.setState(
      (state) => reducers.postcodeChanged(newValue, state),
      () => {
        if (isValid === true) {
          this.notifyParentOfFilterChanges();
        }
      },
    );
  };

  render() {
    const { fuelEligibilityStatus, addressApiProps, updatePlans, address } = this.props;
    const { fuelsRequired, hasSolarPanels, postcode, renewableEnergy, isBusiness } = this.state;

    const isElectricityEligibleForPurchase = isFuelEligibleForPurchase(
      fuelEligibilityStatus.electricity,
    );
    const isGasEligibleForPurchase = isFuelEligibleForPurchase(fuelEligibilityStatus.gas);
    const requiresElectricity = fuelsRequired.electricity && isElectricityEligibleForPurchase;
    const requiresGas = fuelsRequired.gas && isGasEligibleForPurchase;

    return (
      <Filters
        address={address}
        onAddressChange={updatePlans}
        addressApiProps={addressApiProps}
        handleFuelRequirementChange={this.handleFuelRequirementChange}
        requiresElectricity={requiresElectricity}
        requiresGas={requiresGas}
        disableElectricity={!isElectricityEligibleForPurchase}
        disableGas={!isGasEligibleForPurchase}
        handleHasSolarPanelsChange={this.handleHasSolarPanelsChange}
        hasSolarPanels={hasSolarPanels}
        handlePostcodeChange={this.handlePostcodeChange}
        postcode={postcode}
        handleRenewableEnergyChange={this.handleRenewableEnergyChange}
        renewableEnergy={renewableEnergy}
        lpgCrossSellLink={this.props.lpgCrossSellLink}
        handleGasBottleLinkClick={this.handleGasBottleLinkClick}
        isBusiness={isBusiness}
      />
    );
  }
}
